import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@stores/appStore';
import {
  availableLanguages,
  getDirectionForLanguage,
  type LanguageCode,
} from '@/i18n';

/**
 * Hook for managing language switching in Nour Al-Arabia
 *
 * This hook synchronizes:
 * - i18next language state
 * - Zustand app store (locale + direction)
 * - Document attributes (dir, lang)
 *
 * @returns Object with language state and actions
 */
export function useLanguage() {
  const { i18n, t } = useTranslation();
  const { locale, direction, setLocale } = useAppStore();

  /**
   * Change the application language
   * Updates i18next, Zustand store, and document attributes
   */
  const changeLanguage = useCallback(
    async (lang: LanguageCode) => {
      // Update i18next
      await i18n.changeLanguage(lang);

      // Update Zustand store (this also updates direction)
      setLocale(lang);

      // Update document attributes for CSS selectors and accessibility
      const dir = getDirectionForLanguage(lang);
      document.documentElement.dir = dir;
      document.documentElement.lang = lang;
    },
    [i18n, setLocale]
  );

  /**
   * Toggle between Arabic and English
   */
  const toggleLanguage = useCallback(async () => {
    const newLang = locale === 'ar' ? 'en' : 'ar';
    await changeLanguage(newLang);
  }, [locale, changeLanguage]);

  /**
   * Check if the current language is Arabic
   */
  const isArabic = locale === 'ar';

  /**
   * Check if the current direction is RTL
   */
  const isRTL = direction === 'rtl';

  /**
   * Get the current language object with all metadata
   */
  const currentLanguage = availableLanguages.find(
    (lang) => lang.code === locale
  );

  /**
   * Get the other available language (for toggle button display)
   */
  const otherLanguage = availableLanguages.find(
    (lang) => lang.code !== locale
  );

  // Sync i18next with stored locale on mount
  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [i18n, locale]);

  // Sync document attributes when direction changes
  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = locale;
  }, [direction, locale]);

  return {
    /** Current locale code ('ar' | 'en') */
    locale,
    /** Current text direction ('rtl' | 'ltr') */
    direction,
    /** Whether current language is Arabic */
    isArabic,
    /** Whether current direction is RTL */
    isRTL,
    /** Current language metadata object */
    currentLanguage,
    /** The other language metadata object (for toggle display) */
    otherLanguage,
    /** All available languages */
    availableLanguages,
    /** Change to a specific language */
    changeLanguage,
    /** Toggle between Arabic and English */
    toggleLanguage,
    /** Translation function from i18next */
    t,
    /** i18next instance */
    i18n,
  };
}

export type UseLanguageReturn = ReturnType<typeof useLanguage>;
